import React, { useEffect, useRef, useState } from 'react';
import { FaPlus, FaMinus } from 'react-icons/fa'; 
import styles from './MaterialStockForm.module.css';
import DispatchSearch from '../DispatchSearch/DispatchSearch';
import { CiImageOn } from "react-icons/ci";
import ImageModal from "../../components/ImageModal/ImageModal";
import request from '../../utils/request';
import SelectSearch from '../FormComponents/SelectSearch/SelectSearch';
import SelectMaterial from '../SelectMaterial/SelectMaterial';
import ModalLoading from '../ModalLoading/ModalLoading';
import { FiTrash2 } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import t from "../../utils/translation";


const MaterialStockForm = () => {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [inputMaterial, setInputMaterial] = useState('');
  const [quantities, setQuantities] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const inputRefs = useRef({});

  const getMaterials = async () => {
    setLoading(true);
    try {
      const res = await request().get('/api/parameter/material');
      setMaterials(res.data.materials);
      setFilteredMaterials(res.data.materials);
    } catch (err) {
      console.error('Error fetching materials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMaterials();
  }, []);

   const handleQuantityChange = (material, value) => {
    const qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) return;

     if (material.serial && qty > 1) {
    toast.error(t("BlankEntry148") || "Materials with serial number can only have quantity 1");
    return
  }
    setQuantities((prev) => ({ ...prev, [material.materialId]: value }));

    setSelectedMaterials((prev) => {
      const exists = prev.find((m) => m.materialId === material.materialId);

      if (qty === 0 || value === '') {
        return prev.filter((m) => m.materialId !== material.materialId);
      }
      

      const newMaterial = { ...material, quantity: qty };

      if (exists) {
        return prev.map((m) => (m.materialId === material.materialId ? newMaterial : m));
      } else {
        return [...prev, newMaterial];
      }
    });
  };

  const handleBlurOrEnter = (e, material) => {
    const inputValue = e.target?.value;
    const qty = parseInt(inputValue, 10);
    if (!isNaN(qty) && qty > 0) {
      handleQuantityChange(material, qty);
    }
  };

  const handleKeyDown = (e, index, material) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlurOrEnter(e, material);
      const nextMaterial = filteredMaterials[index + 1];
      if (nextMaterial && inputRefs.current[nextMaterial.materialId]) {
        inputRefs.current[nextMaterial.materialId].focus();
      }
    }
  };

  const handleDeleteMaterial = (materialId) => {
    setSelectedMaterials((prev) => prev.filter((m) => m.materialId !== materialId));
    setQuantities((prev) => {
      const newQuantities = { ...prev };
      delete newQuantities[materialId];
      return newQuantities;
    });
  };

  const handleOpenImage = (imageUrl) => {
    setCurrentImage(imageUrl);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentImage(null);
  };

  const handleSubmit = async () => {
    if (selectedMaterials.length === 0) {
      setError(t("BlankEntry149") ||'You must add at least one material with a valid quantity.');
      return;
    }

    try {
      await request().post(
        '/api/materialStock',
        selectedMaterials.map(({ materialId, quantity }) => ({
          materialId,
          quantity,
        }))
      );
      toast.success(t("BlankEntry150") || 'Stock added successfully');
      setSelectedMaterials([]);
      setQuantities({});
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar.');
    }
  };

  const handleSelectMaterial = (selected) => {
    if (!selected) {
      setFilteredMaterials(materials);
      return;
    }
    setInputMaterial(selected.materialName);
    const filtered = materials.filter((m) =>
      m.materialName
        .toLowerCase()
        .includes(selected.materialName.toLowerCase())
    );
    setFilteredMaterials(filtered);
  };

  return (
    <div>
      <ModalLoading open={loading} />
      <ToastContainer />
      <div className={styles.searchContainer}>
        <SelectMaterial
          options={materials}
          idProp="materialId"
          labelProp="materialName"
          placeholder= {t("BlankEntry36") || "Search material"}
          onOptionSelect={handleSelectMaterial}
        />
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div style={{ display: 'flex', gap: '2rem' }}>
        <div className={styles.container} style={{ flex: 1 }}>
          <table className={styles.materialTable}>
            <thead>
              <tr>
                <th>{t("NameSpan")}</th>
                <th>{t("SerialSpan")}</th>
                <th>{t("QuantitySpan")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMaterials.map((material, index) => (
                <tr key={material.materialId}>
                  <td>{material.materialName}</td>
                  <td>{material.serial || 'N/A'}</td>
                  <td>
                    <div className={styles.inputGroup}>
                      <button
                        className={styles.button}
                        onClick={() =>
                          handleQuantityChange(
                            material,
                            (quantities[material.materialId] || 0) - 1
                          )
                        }
                      >
                        -
                      </button>
                      <input
                        type="number"
                        className={styles.input}
                        value={quantities[material.materialId] || ''}
                        ref={(el) =>
                          (inputRefs.current[material.materialId] = el)
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          handleQuantityChange(material, value === '' ? 0 : value);
                        }}
                        onBlur={(e) => handleBlurOrEnter(e, material)}
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, material)
                        }
                      />
                      <button
                        className={styles.button}
                        onClick={() =>
                          handleQuantityChange(
                            material,
                            (quantities[material.materialId] || 0) + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards container */}
        <div className={styles.cardContainer}>
          <h4 className={styles.cardTitle}>{t("MaterialSpan")}</h4>
          {selectedMaterials.length === 0 ? (
            <p className={styles.emptyMessage}>
             {t("BlankEntry146") || "There are no added materials"}
            </p>
          ) : (
            selectedMaterials.map((mat) => (
              <div key={mat.materialId} className={styles.materialCard}>
                <div className={styles.cardHeader}>
                  <strong className={styles.materialName}>
                    {mat.materialName}
                  </strong>
                  <div className={styles.iconContainer}>
                    <CiImageOn
                      className={styles.imageButtonImg}
                      onClick={() => handleOpenImage(mat.materialImageLink)}
                    />
                    <FiTrash2
                      className={styles.imageButtonDelete}
                      onClick={() => handleDeleteMaterial(mat.materialId)}
                    />
                  </div>
                </div>
                <div className={styles.cardRow}>
                  <span className={styles.label}>{t("SerialSpan")}</span>
                  <span className={styles.value}>{mat.serial || 'N/A'}</span>
                </div>
                <div className={styles.cardRow}>
                  <span className={styles.label}>{t("QuantitySpan")}</span>
                  <span className={styles.value}>{mat.quantity}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {modalOpen && currentImage && (
        <ImageModal imageSrc={currentImage} onClose={handleCloseModal} />
      )}

      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}
      >
        <button className={styles.button} onClick={handleSubmit}>
          {t("AddButton") || "Add"}
        </button>
      </div>
    </div>
  );
};

export default MaterialStockForm;